import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {useDispatch} from 'react-redux'
import {useConditionalEffect} from 'hooks/useConditionalEffect'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {usePriceFilter} from 'hooks/usePriceFilter'
import {getPriceFilterVisibility} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {searchParamToFacet} from 'modules/search/constants'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {PriceFilterBottomSheetContentLoadable} from './Loadable'

const filterKeys = [
  searchParamToFacet.priceMin.param,
  searchParamToFacet.priceMax.param
]

export const PriceFilterBottomSheet = () => {
  const dispatch = useDispatch()
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()
  const {
    submitFilters,
    getFilters,
    clearFilters,
    resetFilters,
    calculateIfFiltersChanged,
    toggleFilters
  } = useFiltersForm(filterKeys, 'price')
  const {title: priceFilterTitle} = usePriceFilter()

  const {priceMin, priceMax} = getFilters()
  const isOpen = useSelector(getPriceFilterVisibility)

  const handleApply = () => {
    if (calculateIfFiltersChanged(getFilters())) {
      submitFilters()
    } else {
      dispatch(hideOverlay())
    }
  }

  const handleClear = () => {
    if (priceMax || priceMin) {
      clearFilters()
    } else {
      dispatch(hideOverlay())
    }
  }

  const handleClose = () => {
    dispatch(hideOverlay())
    resetFilters()
  }

  useConditionalEffect(
    async () =>
      import(
        /* webpackPrefetch: true */
        /* webpackChunkName: "PriceFilterBottomSheetContent" */
        './PriceFilterBottomSheetContent'
      ),
    isMobile
  )

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      ignoreSnapPoints
      navBarProps={{
        centerContent: formatMessage(priceFilterTitle)
      }}
      bottomBarContent={
        <Box
          display="flex"
          justifyContent="space-between"
          data-id="priceFilterBottomSheetButtons"
        >
          <Button
            dataId="clearFiltersButton"
            onClick={handleClear}
            variant="tertiary"
          >
            <FormattedMessageWrapper id="clear" defaultMessage="Clear" />
          </Button>
          <Button dataId="applyFiltersButton" onClick={handleApply}>
            <FormattedMessageWrapper id="apply" defaultMessage="Apply" />
          </Button>
        </Box>
      }
    >
      {isOpen && (
        <PriceFilterBottomSheetContentLoadable
          priceMin={priceMin}
          priceMax={priceMax}
          toggleFilters={toggleFilters}
        />
      )}
    </BottomSheet>
  )
}
